/**
 * DiseaseSuggestionPanel.jsx
 *
 * Floating, draggable panel on the PrescriptionPad.
 * Watches the COMPLAINTS box automatically (debounced 650ms).
 * Calls GET /api/diseases/suggest?q=<complaints text>
 *
 * Each result from the backend carries:
 *   matchedPurvarupa[]  — which prodromal symptoms matched
 *   matchedRupa[]       — which cardinal symptoms matched
 *   stage               — "purvarupa" | "rupa" | "both" | null
 *   matchCount          — total matched entries (used for ranking)
 *
 * The panel shows a STAGE badge next to each disease:
 *   Purvarupa  → amber   "Prodromal stage"
 *   Rupa       → red     "Established"
 *   Both       → purple  "Confirmed"
 *
 * Expanding a card reveals the matched terms in two separate
 * colour-coded sections (amber = purvarupa, red = rupa) plus the
 * full lists so the doctor can see unmatched symptoms too.
 *
 * Guard: returns null unless doctorUser.hasDiseaseSuggestions === true
 *
 * Props:
 *   complaintsHtml  — raw HTML string from the Complaints TypedEditor
 *   doctorUser      — object from sessionStorage ("doctorUser")
 */

import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";

/* ── plain-text strip ── */
function htmlToText(html = "") {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ").trim();
}

/* ── simple debounce hook ── */
function useDebounced(value, delay) {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
}

function copyText(t) { try { navigator.clipboard.writeText(t); } catch {} }

/* ── stage badge config ── */
const STAGE_CFG = {
  purvarupa: { label: "Prodromal",   bg: "#FFFBEB", border: "#FDE68A", text: "#92400E", dot: "#F59E0B" },
  rupa:      { label: "Established", bg: "#FFF1F2", border: "#FECDD3", text: "#9F1239", dot: "#F43F5E" },
  both:      { label: "Confirmed",   bg: "#F5F3FF", border: "#DDD6FE", text: "#5B21B6", dot: "#7C3AED" },
};

/* ── match score badge colours ── */
function badgeBg(n)    { return n >= 4 ? "#DCFCE7" : n >= 2 ? "#FEF9C3" : "#FEE2E2"; }
function badgeColor(n) { return n >= 4 ? "#166534" : n >= 2 ? "#854D0E" : "#991B1B"; }

export default function DiseaseSuggestionPanel({ complaintsHtml, doctorUser, onInsertDiagnosis, bubbleOffsetY = 84 }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [expanded,    setExpanded]    = useState(null);
  const [copied,      setCopied]      = useState(null);
  const [visible,     setVisible]     = useState(true);
  /* Collapsed-by-default floating bubble (matches Medicine panel behaviour) */
  const [open,        setOpen]        = useState(false);
  const [minimised,   setMinimised]   = useState(false);
  const [pos,         setPos]         = useState(() => {
    try {
      const saved = localStorage.getItem("dsp_pos");
      if (saved) return JSON.parse(saved);
    } catch {}
    return { x: null, y: null };
  });

  const panelRef   = useRef(null);
  const dragRef    = useRef({ dragging: false });
  const dragRafRef = useRef(null);

  /* complaints text, debounced */
  const complaintsText = useDebounced(htmlToText(complaintsHtml), 650);

  /* ── fetch ── */
  useEffect(() => {
    if (!open) return; // no need to query while collapsed to a bubble
    if (!complaintsText || complaintsText.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/diseases/suggest?q=${encodeURIComponent(complaintsText)}`)
      .then(r  => { if (!cancelled) setSuggestions(r.data || []); })
      .catch(() => { if (!cancelled) setSuggestions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [complaintsText, open]);

  /* Panel stays open once expanded — closes only via the header ×
     button (see below). Clicking elsewhere must NOT dismiss it. */


  /* ── drag ── */
  const onDrag = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
    dragRafRef.current = requestAnimationFrame(() => {
      setPos({
        x: dragRef.current.startLeft + (e.clientX - dragRef.current.startX),
        y: dragRef.current.startTop  + (e.clientY - dragRef.current.startY),
      });
    });
  }, []);

  const endDrag = useCallback(() => {
    dragRef.current.dragging = false;
    window.removeEventListener("pointermove", onDrag);
    window.removeEventListener("pointerup",   endDrag);
    setPos(current => {
      if (current.x !== null) {
        try { localStorage.setItem("dsp_pos", JSON.stringify(current)); } catch {}
      }
      return current;
    });
  }, [onDrag]);

  const startDrag = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startLeft: rect.left, startTop: rect.top };
    window.addEventListener("pointermove", onDrag);
    window.addEventListener("pointerup",   endDrag);
  }, [onDrag, endDrag]);

  useEffect(() => () => {
    window.removeEventListener("pointermove", onDrag);
    window.removeEventListener("pointerup",   endDrag);
    if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
  }, [onDrag, endDrag]);

  const handleCopy = (name) => {
    copyText(name);
    setCopied(name);
    setTimeout(() => setCopied(null), 1800);
  };

  /* ── guard ── */
  if (!doctorUser?.hasDiseaseSuggestions) return null;
  if (!visible) return null;

  /* ── Collapsed bubble (default state) — stacked above the Medicine
     bubble on the right edge, never obstructs typing ── */
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Disease Suggestions"
        style={{
          position: "fixed", zIndex: 10001,
          bottom: 24 + bubbleOffsetY, right: 24,
          width: 52, height: 52, borderRadius: "50%",
          background: "linear-gradient(135deg, #7C3AED, #9333EA)",
          border: "none", boxShadow: "0 8px 24px rgba(124,58,237,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, cursor: "pointer",
        }}
      >
        🩺
      </button>
    );
  }

  const panelStyle = {
    position: "fixed", zIndex: 10001,           /* one above MedicinePanel */
    width: minimised ? 250 : 370,
    ...(pos.x !== null
      ? { left: pos.x, top: pos.y }
      : { bottom: 24 + bubbleOffsetY, right: 24 }),
  };

  const hasSuggestions = suggestions.length > 0;

  return (
    <div ref={panelRef} style={panelStyle}>
      <div style={{
        background: "#fff",
        border: "1.5px solid #E2E8F0",
        borderRadius: 18,
        boxShadow: "0 12px 48px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.07)",
        overflow: "hidden",
        fontFamily: "'Inter', system-ui, sans-serif",
        transition: "width 0.2s ease",
      }}>

        {/* ── Header ── */}
        <div
          onPointerDown={startDrag}
          style={{
            background: "linear-gradient(135deg, #7C3AED, #9333EA)",
            padding: "11px 14px", cursor: "grab",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            userSelect: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 15 }}>🔬</span>
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              Disease Suggest
            </span>
            {loading && <span style={pulseStyle} />}
          </div>
          <div style={{ display: "flex", gap: 5 }}>
            <button onPointerDown={e => e.stopPropagation()} onClick={() => setMinimised(m => !m)} style={hdrBtn}>
              {minimised ? "▲" : "−"}
            </button>
            <button onPointerDown={e => e.stopPropagation()} onClick={() => setOpen(false)} title="Collapse to bubble" style={hdrBtn}>×</button>
          </div>
        </div>

        {!minimised && (<>

          {/* ── Source indicator ── */}
          <div style={{ padding: "8px 14px 0", fontSize: 10, color: "#94A3B8", display: "flex", alignItems: "center", gap: 5 }}>
            {complaintsText.length >= 2
              ? <><span style={{ color: "#22C55E" }}>●</span> Auto-reading Complaints: "{complaintsText.slice(0, 44)}{complaintsText.length > 44 ? "…" : ""}"</>
              : <><span style={{ color: "#E5E7EB" }}>●</span> Waiting for text in <strong style={{ color: "#64748B" }}>Complaints</strong>…</>
            }
          </div>

          {/* ── Legend ── */}
          {hasSuggestions && (
            <div style={{ margin: "6px 12px 0", display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(STAGE_CFG).map(([key, cfg]) => (
                <span key={key} style={{
                  display: "flex", alignItems: "center", gap: 4,
                  background: cfg.bg, border: `1px solid ${cfg.border}`,
                  borderRadius: 20, padding: "2px 8px",
                  fontSize: 9.5, fontWeight: 700, color: cfg.text,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
                  {cfg.label}
                </span>
              ))}
            </div>
          )}

          {/* ── Hint ── */}
          {complaintsText.length >= 2 && (
            <div style={{ margin: "6px 12px 0", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "5px 10px", fontSize: 10, color: "#5B21B6" }}>
              💡 Matches both <strong>Purvarupa</strong> (prodromal) and <strong>Rupa</strong> (cardinal) symptoms
            </div>
          )}

          {/* ── No results ── */}
          {!loading && complaintsText.length >= 2 && !hasSuggestions && (
            <div style={{ padding: "20px 16px", textAlign: "center", color: "#94A3B8", fontSize: 12 }}>
              No matching diseases found
            </div>
          )}

          {/* ── Results ── */}
          {hasSuggestions && (
            <div style={{ maxHeight: 460, overflowY: "auto", padding: "6px 0 4px" }}>
              {suggestions.map((disease, i) => {
                const isOpen = expanded === disease._id;
                const stageCfg = STAGE_CFG[disease.stage] || null;

                return (
                  <div key={disease._id} style={{ borderBottom: i < suggestions.length - 1 ? "1px solid #F1F5F9" : "none" }}>

                    {/* Card header */}
                    <div
                      onClick={() => setExpanded(isOpen ? null : disease._id)}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = "#FAFBFF"; }}
                      onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = "transparent"; }}
                      style={{
                        padding: "10px 14px", cursor: "pointer",
                        display: "flex", alignItems: "flex-start", gap: 10,
                        background: isOpen ? "#FAFBFF" : "transparent",
                      }}
                    >
                      {/* Match score badge */}
                      <div style={{
                        minWidth: 28, height: 28, borderRadius: 8,
                        background: badgeBg(disease.matchCount),
                        color: badgeColor(disease.matchCount),
                        fontSize: 12, fontWeight: 900,
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        {disease.matchCount}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Name + note icon */}
                        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1E293B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 175 }}>
                            {disease.name}
                          </span>
                          {disease.note && (
                            <span title={disease.note} style={{ fontSize: 13, color: "#F59E0B", cursor: "help", flexShrink: 0 }}>⚠</span>
                          )}
                        </div>

                        {/* Stage badge */}
                        {stageCfg && (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: stageCfg.bg, border: `1px solid ${stageCfg.border}`,
                            borderRadius: 20, padding: "2px 8px",
                            fontSize: 9.5, fontWeight: 700, color: stageCfg.text, marginBottom: 4,
                          }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: stageCfg.dot, display: "inline-block" }} />
                            {stageCfg.label}
                          </span>
                        )}

                        {/* Quick matched pills — purvarupa amber, rupa red */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                          {disease.matchedPurvarupa.slice(0, 2).map((t, ti) => (
                            <span key={`p-${ti}`} style={purvarPill}>{t}</span>
                          ))}
                          {disease.matchedRupa.slice(0, 2).map((t, ti) => (
                            <span key={`r-${ti}`} style={rupaPill}>{t}</span>
                          ))}
                          {(disease.matchedPurvarupa.length + disease.matchedRupa.length) > 4 && (
                            <span style={{ fontSize: 9, color: "#94A3B8" }}>
                              +{disease.matchedPurvarupa.length + disease.matchedRupa.length - 4}
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                        <button
                          onClick={e => { e.stopPropagation(); handleCopy(disease.name); }}
                          style={{
                            background: copied === disease.name ? "#DCFCE7" : "#F1F5F9",
                            border: "none", borderRadius: 6, padding: "3px 8px",
                            fontSize: 9.5, fontWeight: 700, cursor: "pointer",
                            color: copied === disease.name ? "#166534" : "#64748B",
                          }}
                        >
                          {copied === disease.name ? "✓ Copied" : "Copy"}
                        </button>
                        {onInsertDiagnosis && (
                          <button
                            onClick={e => { e.stopPropagation(); onInsertDiagnosis(disease.name); }}
                            style={{
                              background: "#EDE9FE",
                              border: "none", borderRadius: 6, padding: "3px 8px",
                              fontSize: 9.5, fontWeight: 700, cursor: "pointer",
                              color: "#5B21B6", whiteSpace: "nowrap",
                            }}
                          >
                            → Diagnosis
                          </button>
                        )}
                        <span style={{ fontSize: 10, color: "#94A3B8" }}>{isOpen ? "▲" : "▼"}</span>
                      </div>
                    </div>

                    {/* ── Expanded detail ── */}
                    {isOpen && (
                      <div style={{ padding: "0 14px 14px", background: "#FAFBFF", borderTop: "1px solid #EDE9FE" }}>

                        {/* Clinical note */}
                        {disease.note && (
                          <div style={{ margin: "10px 0", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 9, padding: "8px 12px" }}>
                            <div style={{ fontSize: 9, fontWeight: 800, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 3 }}>⚠ Clinical Note</div>
                            <div style={{ fontSize: 11, color: "#78350F", lineHeight: 1.65 }}>{disease.note}</div>
                          </div>
                        )}

                        {/* Matched Purvarupa */}
                        {disease.matchedPurvarupa.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={secLbl}>🌅 Matched Purvarupa — Prodromal ({disease.matchedPurvarupa.length})</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                              {disease.matchedPurvarupa.map((s, si) => (
                                <span key={si} style={{ ...purvarPill, fontSize: 10.5 }}>{s}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Matched Rupa */}
                        {disease.matchedRupa.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={secLbl}>🔴 Matched Rupa — Cardinal ({disease.matchedRupa.length})</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                              {disease.matchedRupa.map((s, si) => (
                                <span key={si} style={{ ...rupaPill, fontSize: 10.5 }}>{s}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Full Purvarupa list (unmatched greyed) */}
                        {disease.purvarupa?.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={secLbl}>All Purvarupa ({disease.purvarupa.length})</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                              {disease.purvarupa.map((s, si) => {
                                const matched = disease.matchedPurvarupa.includes(s);
                                return (
                                  <span key={si} style={{
                                    fontSize: 10, borderRadius: 20, padding: "2px 8px",
                                    background: matched ? "#FFFBEB" : "#F9FAFB",
                                    border: `1px solid ${matched ? "#FDE68A" : "#E5E7EB"}`,
                                    color: matched ? "#92400E" : "#9CA3AF",
                                    fontWeight: matched ? 700 : 400,
                                  }}>{s}</span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Full Rupa list (unmatched greyed) */}
                        {disease.rupa?.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={secLbl}>All Rupa ({disease.rupa.length})</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                              {disease.rupa.map((s, si) => {
                                const matched = disease.matchedRupa.includes(s);
                                return (
                                  <span key={si} style={{
                                    fontSize: 10, borderRadius: 20, padding: "2px 8px",
                                    background: matched ? "#FFF1F2" : "#F9FAFB",
                                    border: `1px solid ${matched ? "#FECDD3" : "#E5E7EB"}`,
                                    color: matched ? "#9F1239" : "#9CA3AF",
                                    fontWeight: matched ? 700 : 400,
                                  }}>{s}</span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Footer ── */}
          <div style={{ padding: "6px 14px", borderTop: "1px solid #F1F5F9", background: "#FAFBFD", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 9.5, color: "#CBD5E1" }}>
              {hasSuggestions ? `${suggestions.length} result${suggestions.length !== 1 ? "s" : ""}` : ""}
            </span>
            <span style={{ fontSize: 9, color: "#CBD5E1", fontStyle: "italic" }}>
              purvarupa + rupa · auto-updates
            </span>
          </div>

        </>)}
      </div>
      <style>{`@keyframes dsp-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(.55)}}`}</style>
    </div>
  );
}

/* ── Styles ── */
const pulseStyle = { width: 8, height: 8, borderRadius: "50%", background: "#C4B5FD", display: "inline-block", animation: "dsp-pulse 0.9s ease-in-out infinite" };
const hdrBtn     = { background: "rgba(255,255,255,.18)", border: "none", color: "#fff", borderRadius: 6, width: 22, height: 22, fontSize: 14, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
const secLbl     = { fontSize: 9, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" };
const purvarPill = { fontSize: 9.5, background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A", borderRadius: 20, padding: "2px 7px", fontWeight: 700 };
const rupaPill   = { fontSize: 9.5, background: "#FFF1F2", color: "#9F1239", border: "1px solid #FECDD3", borderRadius: 20, padding: "2px 7px", fontWeight: 700 };